import { Controller } from "@hotwired/stimulus"
import { hasOpenReactionPicker } from "../utils/reaction_picker"

// Live refresh for the Share feed — same { ok, fingerprint, html } contract
// as peer-chat, but WITHOUT the scroll-to-bottom: the feed is newest-first,
// so the user stays at the top and new posts/likes/comments swap in place.
// A poll is skipped while the user is composing or typing a comment, or has a
// reaction picker open, so an in-flight refresh can't wipe what they're doing.
export default class extends Controller {
    static targets = ["thread", "more"]
    static values = {
        url: String,
        olderUrl: String,
        interval: { type: Number, default: 8000 },
        initialFingerprint: String
    }

    connect() {
        this._fingerprint = this.hasInitialFingerprintValue ? this.initialFingerprintValue : null
        // The oldest post currently on screen. null while the reader is on the
        // first page; set once they load older ones, and handed to the poll so
        // a refresh re-renders THEIR span rather than collapsing the feed back
        // to page one underneath them.
        this._oldestId = null
        this._loading = false
        this._timer = setInterval(() => this._poll(), this.intervalValue)
    }

    disconnect() {
        clearInterval(this._timer)
    }

    // "Show older posts" — appends the next page and replaces the button with
    // whatever that page brought (a new button, or nothing at the end).
    loadOlder(event) {
        const button = event.currentTarget
        const before = button.dataset.before
        if (!before || this._loading) return

        this._loading = true
        button.disabled = true
        button.textContent = "Loading…"

        fetch(this._olderUrlFor(before), { headers: { Accept: "application/json" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !data.ok) return this._restore(button)
                const container = button.closest(".reflection-feed-more")
                container.insertAdjacentHTML("afterend", data.html)
                container.remove()
                // Widen the poll's window to everything now on screen.
                if (data.oldest_id) this._oldestId = data.oldest_id
                // The appended page changes what a refresh should return, so the
                // next poll must not be skipped as "unchanged".
                this._fingerprint = null
            })
            .catch(() => this._restore(button))
            .finally(() => { this._loading = false })
    }

    _restore(button) {
        button.disabled = false
        button.textContent = "Show older posts"
    }

    _olderUrlFor(before) {
        const url = new URL(this.olderUrlValue, window.location.origin)
        url.searchParams.set("before", before)
        return url
    }

    _poll() {
        if (document.hidden) return
        if (this._isComposing()) return
        // Don't race a page append: the response would be built against a span
        // that is about to change.
        if (this._loading) return

        const url = new URL(this.urlValue, window.location.origin)
        if (this._oldestId) url.searchParams.set("through", this._oldestId)
        // Sending what we already have lets the server answer "nothing moved"
        // from three columns instead of querying, instantiating and rendering
        // every card on screen only for us to throw the HTML away. That is the
        // shape of nearly every poll.
        if (this._fingerprint) url.searchParams.set("fingerprint", this._fingerprint)

        fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !data.ok) return
                if (data.unchanged || this._fingerprint === data.fingerprint) return
                // A refresh that landed while the reader was mid-append would
                // overwrite it; _loading is checked again because the fetch is
                // in flight across that window.
                if (this._loading) return
                // Same for an open reaction picker: the swap would close it
                // mid-selection. The fingerprint stays stale so the next poll
                // applies the change once it's closed.
                if (hasOpenReactionPicker(this.threadTarget)) return
                this._fingerprint = data.fingerprint
                this.threadTarget.innerHTML = data.html
            })
            .catch(() => {})
    }

    // Don't clobber a focused textarea/input inside the feed (a half-written
    // comment) with a poll refresh.
    _isComposing() {
        let active = document.activeElement
        return active && this.element.contains(active) && ["TEXTAREA", "INPUT"].includes(active.tagName)
    }
}
